import { supabase } from './supabase';

// ── PROVIDER PROFILE ───────────────────────────────────────

export const saveProfile = async (userId, profile) => {
  const { data, error } = await supabase
    .from('providers')
    .upsert({
      id: userId,
      email: profile.email || '',
      daycare_name: profile.daycareN,
      provider_name: profile.providerN,
      license_number: profile.licenseN,
      ssps_number: profile.sspsN,
      address: profile.address,
      city: profile.city,
      zip: profile.zip,
      capacity: parseInt(profile.capacity) || 6,
      wccc: profile.wccc,
      plan: profile.bundle || 'monthly'
    });
  if (error) console.error('Error saving profile:', error);
  return { data, error };
};

export const loadProfile = async (userId) => {
  const { data, error } = await supabase
    .from('providers')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) console.error('Error loading profile:', error);
  return data;
};

// ── CHILDREN ───────────────────────────────────────────────

export const saveChild = async (providerId, child) => {
  const { data, error } = await supabase
    .from('children')
    .insert({
      provider_id: providerId,
      name: child.name,
      age: child.age,
      parent_name: child.parent,
      parent_phone: child.parentPhone,
      parent_email: child.parentEmail,
      notify_via: child.notify || 'sms',
      auth_type: child.auth,
      age_group: child.ageGroup,
      transport: child.transport,
      destination: child.destination,
      ec1_name: child.ec1Name,
      ec1_phone: child.ec1Phone,
      ec1_rel: child.ec1Rel,
      ec2_name: child.ec2Name,
      ec2_phone: child.ec2Phone,
      ec2_rel: child.ec2Rel,
      photo: child.photo || null,
    });
  if (error) console.error('Error saving child:', error);
  return { data, error };
};

export const loadChildren = async (providerId) => {
  const { data, error } = await supabase
    .from('children')
    .select('*')
    .eq('provider_id', providerId)
    .eq('active', true)
    .order('name');
  if (error) console.error('Error loading children:', error);
  return data || [];
};

export const updateChild = async (childId, updates) => {
  const { data, error } = await supabase
    .from('children')
    .update({
      name: updates.name,
      age: updates.age,
      parent_name: updates.parent,
      parent_phone: updates.parentPhone,
      parent_email: updates.parentEmail,
      notify_via: updates.notify,
      auth_type: updates.auth,
      transport: updates.transport,
      destination: updates.destination,
      ec1_name: updates.ec1Name,
      ec1_phone: updates.ec1Phone,
      ec1_rel: updates.ec1Rel,
      ec2_name: updates.ec2Name,
      ec2_phone: updates.ec2Phone,
      ec2_rel: updates.ec2Rel,
      photo: updates.photo || null,
    })
    .eq('id', childId);
  if (error) console.error('Error updating child:', error);
  return { data, error };
};

export const removeChild = async (childId) => {
  const { error } = await supabase
    .from('children')
    .update({ active: false })
    .eq('id', childId);
  if (error) console.error('Error removing child:', error);
  return { error };
};

// ── STAFF ──────────────────────────────────────────────────

export const saveStaff = async (providerId, member) => {
  const { data, error } = await supabase
    .from('staff')
    .insert({
      provider_id: providerId,
      name: member.name,
      role: member.role,
      pin: member.pin,
      driver: member.driver,
    });
  if (error) console.error('Error saving staff:', error);
  return { data, error };
};

export const loadStaff = async (providerId) => {
  const { data, error } = await supabase
    .from('staff')
    .select('*')
    .eq('provider_id', providerId)
    .eq('active', true)
    .order('name');
  if (error) console.error('Error loading staff:', error);
  return data || [];
};

// ── STAFF ATTENDANCE ───────────────────────────────────────

export const loadTodayStaffAttendance = async (providerId) => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
  const end   = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();
  const { data, error } = await supabase
    .from('staff_attendance')
    .select('staff_id, clock_in_time, clock_out_time')
    .eq('provider_id', providerId)
    .gte('clock_in_time', start)
    .lt('clock_in_time', end)
    .order('clock_in_time', { ascending: false });
  if (error) console.error('Error loading today staff attendance:', error);

  // Return only the LATEST record per staff member
  const latest = {};
  for (const row of (data || [])) {
    if (!latest[row.staff_id]) {
      latest[row.staff_id] = row;
    }
  }
  return Object.values(latest);
};

export const recordStaffClockIn = async (providerId, staffId) => {
  const { data, error } = await supabase
    .from('staff_attendance')
    .insert({
      provider_id: providerId,
      staff_id: staffId,
      clock_in_time: new Date().toISOString(),
    });
  if (error) console.error('Error recording staff clock in:', error);
  return { data, error };
};

export const recordStaffClockOut = async (providerId, staffId) => {
  const { data, error } = await supabase
    .from('staff_attendance')
    .update({ clock_out_time: new Date().toISOString() })
    .eq('provider_id', providerId)
    .eq('staff_id', staffId)
    .is('clock_out_time', null);
  if (error) console.error('Error recording staff clock out:', error);
  return { data, error };
};

// ── CHILD ATTENDANCE ───────────────────────────────────────

export const recordSignIn = async (providerId, childId, signatureData, signatureUrl) => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
  const end   = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();
  const { data: existing } = await supabase
    .from('attendance')
    .select('id, sign_out_time')
    .eq('provider_id', providerId)
    .eq('child_id', childId)
    .gte('sign_in_time', start)
    .lt('sign_in_time', end)
    .is('sign_out_time', null)
    .limit(1);
  if (existing && existing.length > 0) {
    console.log('Sign-in skipped — child already signed in today');
    return { data: existing, error: null };
  }
  const { data, error } = await supabase
    .from('attendance')
    .insert({
      provider_id: providerId,
      child_id: childId,
      sign_in_time: new Date().toISOString(),
      signature_url: signatureUrl || null,
    });
  if (error) console.error('Error recording sign in:', error);
  return { data, error };
};

export const recordSignOut = async (providerId, childId, signatureUrl) => {
  const { data, error } = await supabase
    .from('attendance')
    .update({ sign_out_time: new Date().toISOString(), signature_url: signatureUrl || null })
    .eq('provider_id', providerId)
    .eq('child_id', childId)
    .is('sign_out_time', null);
  if (error) console.error('Error recording sign out:', error);
  return { data, error };
};

export const loadTodayAttendance = async (providerId) => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
  const end   = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();

  // Load today's attendance records
  const { data: todayData, error } = await supabase
    .from('attendance')
    .select('child_id, sign_in_time, sign_out_time')
    .eq('provider_id', providerId)
    .gte('sign_in_time', start)
    .lt('sign_in_time', end)
    .order('sign_in_time', { ascending: false });
  if (error) console.error('Error loading today attendance:', error);

  // Also load any records from previous days with no sign-out (missed sign-outs)
  const { data: missedData } = await supabase
    .from('attendance')
    .select('child_id, sign_in_time, sign_out_time')
    .eq('provider_id', providerId)
    .lt('sign_in_time', start)
    .is('sign_out_time', null)
    .order('sign_in_time', { ascending: false });

  const data = [...(todayData || []), ...(missedData || [])];

  // Return only the LATEST record per child
  const latest = {};
  for (const row of data) {
    if (!latest[row.child_id]) {
      latest[row.child_id] = row;
    }
  }
  return Object.values(latest);
};

export const loadAttendance = async (providerId, startDate, endDate) => {
  const { data, error } = await supabase
    .from('attendance')
    .select('*, children(name, auth_type)')
    .eq('provider_id', providerId)
    .gte('sign_in_time', startDate)
    .lte('sign_in_time', endDate)
    .order('sign_in_time', { ascending: false });
  if (error) console.error('Error loading attendance:', error);
  return data || [];
};

export const exportAttendanceCSV = async (providerId, providerInfo) => {
  const { data, error } = await supabase
    .from('attendance')
    .select('*, children(name, auth_type)')
    .eq('provider_id', providerId)
    .order('sign_in_time', { ascending: false });

  if (error) { console.error('Error exporting:', error); return; }

  const rows = [
    ['Provider Name', 'Daycare Name', 'License Number', 'SSPS Number'],
    [providerInfo.providerN, providerInfo.daycareN, providerInfo.licenseN, providerInfo.sspsN],
    [''],
    ['Child Name', 'Payment Type', 'Date', 'Sign In Time', 'Sign Out Time'],
    ...data.map(r => [
      r.children?.name || '',
      r.children?.auth_type || '',
      new Date(r.sign_in_time).toLocaleDateString(),
      new Date(r.sign_in_time).toLocaleTimeString(),
      r.sign_out_time ? new Date(r.sign_out_time).toLocaleTimeString() : 'Still in care',
    ])
  ];

  const csv = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Toto_Attendance_${new Date().toLocaleDateString().replace(/\//g, '-')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── TRANSPORT LOG ───────────────────────────────────────────
export const saveTransportLog = async (providerId, childId, action, destination) => {
  const { supabase } = await import('./supabase');
  const today = new Date().toISOString().split('T')[0];

  // Check if a log entry exists for this child today
  const { data: existing } = await supabase
    .from('transport_log')
    .select('*')
    .eq('provider_id', providerId)
    .eq('child_id', childId)
    .eq('date', today)
    .maybeSingle();

  const now = new Date().toISOString();
  const updates = { destination };
  if (action === 'transport_pickup')  updates.pickup_time  = now;
  if (action === 'transport_dropoff') updates.dropoff_time = now;
  if (action === 'transport_return')  updates.return_time  = now;

  if (existing) {
    await supabase.from('transport_log').update(updates).eq('id', existing.id);
  } else {
    await supabase.from('transport_log').insert({
      provider_id: providerId,
      child_id: childId,
      date: today,
      ...updates,
      sms_sent: true,
    });
  }
};

export const loadTransportLog = async (providerId) => {
  const { supabase } = await import('./supabase');
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('transport_log')
    .select('*, children(name)')
    .eq('provider_id', providerId)
    .eq('date', today)
    .order('created_at', { ascending: true });
  if (error) console.error('Error loading transport log:', error);
  return data || [];
};
